import { useEffect, useMemo, useState } from 'react';
import { type Control, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type { CatalogRequestFormValues } from '@/components/CatalogRequestStepper/config';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNButton from '@ctn/ui/components/ctn-button';
import { useCatalogProductOptions } from '@/hooks';

type FormWithPicker = CatalogRequestFormValues & {
	__bookPicker?: string | number | null;
};

type OrderDataStepProps = {
	readonly title: string;
};

const classNameComponent = 'c-cnm-catalog-stepper';

export const OrderDataStep = ({ title }: OrderDataStepProps) => {
	const { options } = useCatalogProductOptions();

	const { register, setValue, control, formState } = useFormContext<CatalogRequestFormValues>();

	const { fields, append, remove, update } = useFieldArray({
		control,
		name: 'libros',
	});

	// Libros seleccionados actualmente (para filtrar el selector)
	const selectedIds = useMemo(() => new Set<string>(fields.map((f) => String(f.id))), [fields]);
	const availableOptions = useMemo(
		() => options.filter((o) => !selectedIds.has(String(o.value))),
		[options, selectedIds],
	);

	const controlWithPicker = control as unknown as Control<FormWithPicker>;
	const picked = useWatch({ control: controlWithPicker, name: '__bookPicker' });

	useEffect(() => {
		if (picked === null || picked === '') return;
		const opt = options.find((o) => o.value === picked);
		if (!opt) return;

		// Si ya estaba (no debería porque lo filtramos), ignora o suma cantidad
		const exists = fields.findIndex((f) => f.id === opt.value);
		if (exists === -1) {
			append({ id: opt.value, title: opt.label, quantity: 1 });
		}
		setValue('__bookPicker' as any, null, { shouldDirty: true });
	}, [picked, options, fields, append, setValue]);

	const [qtyDraft, setQtyDraft] = useState<Record<number | string, number>>({});

	return (
		<div className={`${classNameComponent}__step`}>
			<h3 className={`${classNameComponent}__step-title`}>{title}</h3>

			<div className="[ o-grid ] [ u-mt-16 ]">
				<div className="o-grid__row">
					<div className="o-grid__col-12">
						<CTNInput
							type="text"
							control={control}
							name="direccion"
							label="Dirección de envío *"
							classNameField={`${classNameComponent}__form-field`}
							classNameInput={`${classNameComponent}__form-input`}
							placeholder="Dirección donde haremos llegar los libros"
							error={formState.errors.direccion?.message}
						/>
					</div>
					<div className="o-grid__col-12">
						<CTNInput
							type="text"
							control={control}
							name="direccionFacturacion"
							label="Dirección de facturación (solo si es distinta a la de envío)"
							classNameField={`${classNameComponent}__form-field`}
							classNameInput={`${classNameComponent}__form-input`}
							placeholder="Dirección donde haremos llegar la factura de los libros"
							error={formState.errors.direccionFacturacion?.message}
						/>
					</div>
					<div className="o-grid__col-12">
						<CTNDropdown<FormWithPicker>
							filter
							showClear
							required
							noFeedBack={false}
							control={controlWithPicker}
							options={availableOptions}
							classNameField={`${classNameComponent}__form-field`}
							classNameInput={`${classNameComponent}__form-input`}
							name="__bookPicker"
							label="LIBRO(S) QUE SE DESEA ADQUIRIR (SELECCIONE EL TÍTULO DEL LIBRO)"
							placeholder="Seleccionar libros"
						/>
					</div>
					<div className="o-grid__col-12">
						{fields.length === 0 && (
							<p className="[ u-mb-16 ]">No hay libros añadidos todavía. Añade al menos uno.</p>
						)}

						<ul className={`${classNameComponent}__form-productList`}>
							{fields.map((field, idx) => (
								<li key={field.id}>
									<div className="[ o-grid ]">
										<div className={`${classNameComponent}__form-productListItem o-grid__row`}>
											<div className="o-grid__col-9">
												<div className={`${classNameComponent}__form-productListTitle`}>
													{field.title}
												</div>
												{/* Mantén los hidden para que el schema valide */}
												<input type="hidden" {...register(`libros.${idx}.title`)} />
												<input type="hidden" {...register(`libros.${idx}.id`)} />
												{formState.errors.libros?.[idx]?.title && (
													<small className="c-error">
														{formState.errors.libros?.[idx]?.title?.message}
													</small>
												)}
												{formState.errors.libros?.[idx]?.id && (
													<small className="c-error">
														{formState.errors.libros?.[idx]?.id?.message}
													</small>
												)}
											</div>
											<div className="o-grid__col-2">
												<CTNInput<CatalogRequestFormValues>
													stepper
													noField
													type="number"
													control={control}
													name={`libros.${idx}.quantity`}
													min={1}
													decimalScale={0}
													fixedDecimalScale={false}
													increment={1}
													classNameInput="c-input"
													error={formState.errors.libros?.[idx]?.quantity?.message}
												/>
											</div>
											<div className="o-grid__col-1">
												<CTNButton
													type="button"
													variant="outline"
													mode="danger"
													icon={[{ icon: 'binOutline', position: 'left', color: 'red-60' }]}
													onClick={() => remove(idx)}
												/>
											</div>
										</div>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
};
